"use strict";

import { trim, forEach, isArray, isString, isNumber, isNil, omit, omitBy, some } from "lodash";

function qs2arr(v) {
  return isArray(v) ? v : [v];
}

function toESMatchDSL(v, k) {
  if (isString(v)) {
    // check surrounding quotes for exact-matching, use `match` instead of `term` so that input will be processed by analyzer
    return (v.startsWith("'") && v.endsWith("'")) || (v.startsWith('"') && v.endsWith('"'))
      ? { match: { [k]: trim(v, "'\"") } }
      // cast boolean string as real boolean
      : v === "true" || v === "false"
        ? { term: { [k]: v } }
        // partial-matching, note that analyzed ES field values are all lowercase;
        // case_insensitive also lets the lowercased term match uppercase keyword fields
        : v
          ? { wildcard: { [k]: { value: trim(v, "'\"").toLowerCase() + "*", case_insensitive: true } } }
          : { match_all: {} };
  } else {
    return isNumber(v) || v == false || v == true // note double equal signs were used for more relaxed comparison
      ? { term: { [k]: v } }
      : { match_all: {} }; // skip if value is not a string, number or boolean
  }
}

function toESRangeDSL(v, k, strParser = v => v) {
  // date range query
  const q = { range: { [k]: {} } };
  let r = v.split("--");
  if (r[0] !== "") q.range[k].gte = strParser(r[0]);
  if (r[1] !== "") q.range[k].lte = strParser(r[1]);
  return q;
}

function toESTermDSL(v, k) {
  return { terms: { [k]: v } };
}

function toESFullTextDSL(v, fields) {
  // fields can be an array of fields or a single field string
  return {
    simple_query_string: {
      query: v,
      fields: qs2arr(fields),
      default_operator: "and"
    }
  };
}

function toESHighlightDSL(fields, n = 5, size = 300) {
  // fields can be an array of fields or a single field string
  // n, show up to <n> fragments
  // size, show up to <size> characters per fragment
  let flds = {};
  const searchIn = qs2arr(fields);
  searchIn.forEach(f => {
    flds[f] = {};
  });
  return {
    highlight: {
      number_of_fragments: n,
      fragment_size: size,
      fields: flds
    }
  };
}

const EXCLUDE_DEFAULT = [
  "created_at",
  "updated_at",
  "id",
  "*.created_at",
  "*.updated_at",
  "*.id",
  "patent_id",
  "patent.abstract",
  "patent.title",
  "*.full_text",
  "claims",
  "claim_findings",
  "claim_outcomes",
  "transactions",
  "documents",
];
const DATE_SUFFIXES = ["_date", "_at", ".date"];
const NUMERIC_SUFFIXES = ["_score", "annuities"];
const FULLTEXT_SUFFIXES = ["full_text", "fulltext", "product"];
const ID_SUFFIXES = ["_id", "_spif"];
const NESTED_PREFIXES = ["filings.", "documents.", "etsi."];

function toESFinalDSL(v, k, fullTextFields) {
  if (v) {
    if (some(ID_SUFFIXES, s => k.endsWith(s))) {
      return toESTermDSL(v, k);
    } else if (some(DATE_SUFFIXES, s => k.endsWith(s))) {
      return toESRangeDSL(v, k);
    } else if (some(NUMERIC_SUFFIXES, s => k.endsWith(s))) {
      return toESRangeDSL(v, k, parseFloat);
    } else if (some(FULLTEXT_SUFFIXES, s => k.endsWith(s))) {
      return toESFullTextDSL(v, fullTextFields || k);
    } else if (isArray(v)) {
      return v.length < 10
        ? { bool: { should: v.filter(ev => !!ev).map(ev => toESMatchDSL(ev, k)) } }
        : toESTermDSL(v, k);
    }
  }
  return toESMatchDSL(v, k);
}

export function toESQuery(obj, exclude = [], size = 20, shouldReturnFullTextHighlight = true, { nestedPrefixes = NESTED_PREFIXES } = {}) {
  let esq = {
    query: { bool: { must: [] } },
    _source: { exclude: EXCLUDE_DEFAULT.concat(exclude) },
    size
  };
  // some fields serve as configs, should be excluded from building DSLs
  const excludedFields = [
    "limit",
    "offset",
    "sort",
    "full_text_fields", // scope to do full text search
    "not" // scope to invert matching
  ];
  // handle pagination
  if (obj.limit) esq.size = obj.limit;
  if (obj.offset) esq.from = obj.offset;
  // handle sort
  if (obj.sort) {
    esq.sort = obj.sort.split(",").map(s =>
      s.startsWith("-")
        ? { [s.substring(1)]: "desc" }
        : { [s]: "asc" }
    );
  }
  // handle boolean operation
  const invertedFields = qs2arr(obj.not || []);
  if (invertedFields.length > 0) {
    esq.query.bool.must_not = [];
  }
  // handle query DSLs
  forEach(omitBy(omit(obj, excludedFields), isNil), (v, k) => {
    let q;
    if (some(nestedPrefixes, p => k.startsWith(p))) {
      const nestedPath = k.split(".").slice(0, -1).join(".");
      q = {
        nested: {
          path: nestedPath,
          query: { bool: { must: [] } },
          inner_hits: {
            _source: { exclude: [`${nestedPath}.full_text`] }, // exclude heavy fields from response
            size: 20
          }
        }
      };
      const qn = toESFinalDSL(v, k, obj.full_text_fields);
      if (qn.simple_query_string && shouldReturnFullTextHighlight) {
        q.nested.inner_hits = {
          ...q.nested.inner_hits,
          ...toESHighlightDSL(obj.full_text_fields || k, 3)
        };
      }
      q.nested.query.bool.must.push(qn);
    } else {
      q = toESFinalDSL(v, k, obj.full_text_fields);
    }
    if (q.simple_query_string && shouldReturnFullTextHighlight) {
      esq = {
        ...esq,
        ...toESHighlightDSL(obj.full_text_fields || k)
      };
    }
    if (q) {
      if (invertedFields.indexOf(k) >= 0) {
        esq.query.bool.must_not.push(q);
      } else {
        esq.query.bool.must.push(q);
      }
    }
  });
  esq.track_total_hits = true;
  return esq;
}

export function toESDateHistogramQuery(fieldDate, interval = "day", queries) {
  let esdhq = toESQuery(queries, [], 0);
  esdhq.aggs = {
    [fieldDate]: {
      date_histogram: {
        field: fieldDate,
        interval: interval,
        format: "yyyy-MM-dd"
      }
    }
  };
  return esdhq;
}

export function toESHistogramQuery(field, interval, queries) {
  let eshq = toESQuery(queries, [], 0);
  eshq.aggs = {
    [field]: {
      histogram: {
        field: field,
        interval: interval
      }
    }
  };
  return eshq;
}

export function toESDateHistogramGroupByQuery(fieldDate, field2, interval = "day", queries, size = 10) {
  let esdhq = toESQuery(queries, [], 0);
  esdhq.aggs = {
    [fieldDate]: {
      date_histogram: {
        field: fieldDate,
        interval: interval,
        format: "yyyy-MM-dd"
      },
      aggs: {
        [field2]: {
          terms: {
            field: field2,
            size: size,
            shard_size: size * 10
          }
        }
      }
    }
  };
  return esdhq;
}

export function toESGroupByQuery(field, queries, size = 10) {
  let esgbq = toESQuery(queries, [], 0);
  let aggsq = {
    [field]: {
      terms: {
        field: field,
        size: size,
        shard_size: size * 10
      }
    }
  };
  esgbq.aggs = aggsq;
  return esgbq;
}

export function toESDoubleGroupByQuery(field1, field2, queries, size = 25) {
  let esgbq = toESQuery(queries, [], 0);
  let aggsq = {
    [field1]: {
      terms: {
        field: field1,
        size: size
      },
      aggs: {
        [field2]: {
          terms: {
            field: field2
          }
        }
      }
    }
  };
  esgbq.aggs = aggsq;
  return esgbq;
}

export function toESTripleGroupByQuery(field1, field2, field3, queries, size = 10) {
  let esgbq = toESQuery(queries, [], 0);
  let aggsq = {
    [field1]: {
      terms: {
        field: field1,
        size
      },
      aggs: {
        [field2]: {
          terms: {
            field: field2,
            size
          },
          aggs: {
            [field3]: {
              terms: {
                field: field3,
                size
              }
            }
          }
        }
      }
    }
  };
  esgbq.aggs = aggsq;
  return esgbq;
}

export function toESAvgsQuery(fields, queries) {
  let esaq = toESQuery(queries, [], 0);
  esaq.aggs = {};
  fields.forEach(field => {
    esaq.aggs[field] = {
      avg: {
        field
      }
    };
  });
  return esaq;
}

export function nestedPathAggs(path, query, field) {
  if (field) {
    // handle deeper level path
    return {
      ...query,
      aggs: {
        [field]: {
          ...query.aggs[field],
          aggs: {
            [path]: {
              nested: {
                path: path
              },
              aggs: query.aggs[field].aggs
            }
          }
        }
      }
    };
  } else {
    return {
      ...query,
      aggs: {
        [path]: {
          nested: {
            path: path
          },
          aggs: query.aggs
        }
      }
    };
  }
}

export function cardinalityAggs(path, query, field, field2) {
  if (field2 && field) {
    return {
      ...query,
      aggs: {
        [field]: {
          ...query.aggs[field],
          aggs: {
            [field2]: {
              ...query.aggs[field].aggs[field2],
              aggs: {
                [path]: {
                  cardinality: {
                    field: path,
                    precision_threshold: 10000
                  }
                }
              }
            }
          }
        }
      }
    };
  } else if (field) {
    return {
      ...query,
      aggs: {
        [field]: {
          ...query.aggs[field],
          aggs: {
            [path]: {
              cardinality: {
                field: path,
                precision_threshold: 10000
              }
            }
          }
        }
      }
    };
  } else {
    return {
      ...query,
      aggs: {
        [path]: {
          cardinality: {
            field: path,
            precision_threshold: 10000
          }
        }
      }
    };
  }
}

export function toESQueryWithAggs(queries, aggs) {
  let esq = toESQuery(queries, [], 0);

  esq.aggs = {};
  aggs.forEach(agg => {
    esq.aggs[agg.name] = {
      filter: agg.filter
    };
  });
  return esq;
}
